import React, { useMemo, useState } from 'react';
import { PlayerHistoryRecord, TransferHistoryRecord } from '../../domain/entities';
import './PlayerValueChart.css';

interface PlayerValueChartProps {
    marketValueHistory: { season: number; week: number; value: number }[];
    careerHistory: PlayerHistoryRecord[];
    transferHistory: TransferHistoryRecord[];
    teamColorsMap: Record<string, string>;
}

const formatValue = (value: number) => {
    if (value >= 1_000_000) return `£${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `£${Math.round(value / 1_000)}K`;
    return `£${value}`;
};

export const PlayerValueChart: React.FC<PlayerValueChartProps> = ({ marketValueHistory, careerHistory, transferHistory, teamColorsMap }) => {
    const [tooltip, setTooltip] = useState<{ x: number, y: number, text: string } | null>(null);

    const chartData = useMemo(() => {
        if (!marketValueHistory || marketValueHistory.length < 1) return null;

        // Calculate actual weeks per season from market value history
        // This accounts for cup matches extending the season
        const weeksBySeason = new Map<number, number>();
        for (const entry of marketValueHistory) {
            const currentMax = weeksBySeason.get(entry.season) || 0;
            if (entry.week > currentMax) {
                weeksBySeason.set(entry.season, entry.week);
            }
        }

        const dataPoints = marketValueHistory.map(h => {
            const weeksInSeason = weeksBySeason.get(h.season) || 42; // Fallback to 42 if no data
            return {
                time: h.season + (h.week / weeksInSeason),
                value: h.value,
                season: h.season,
                week: h.week,
            };
        });

        const transferMarkers = transferHistory.map(t => {
            const weeksInSeason = weeksBySeason.get(t.season) || 42; // Fallback to 42 if no data
            return {
                time: t.season + (t.week / weeksInSeason),
                from: t.fromTeam,
                to: t.toTeam,
                fee: t.fee,
            };
        });

        const times = dataPoints.map(d => d.time);
        const values = dataPoints.map(d => d.value);

        const minTime = Math.min(...times);
        const maxTime = Math.max(...times);
        const minValue = 0;
        const maxValue = values.length > 0 ? Math.max(...values) * 1.1 : 100000;

        const PADDING = { top: 30, right: 20, bottom: 40, left: 60 };
        const WIDTH = Math.max(600, dataPoints.length * 40);
        const HEIGHT = 250;

        const getX = (time: number) => {
            if (maxTime === minTime) return PADDING.left;
            return PADDING.left + ((time - minTime) / (maxTime - minTime)) * (WIDTH - PADDING.left - PADDING.right);
        };
        const getY = (value: number) => {
            if (maxValue === minValue) return HEIGHT - PADDING.bottom;
            return (HEIGHT - PADDING.bottom) - ((value - minValue) / (maxValue - minValue)) * (HEIGHT - PADDING.top - PADDING.bottom);
        };
        
        // Sort data points by time to ensure chronological labeling
        const sortedDataPoints = [...dataPoints].sort((a, b) => a.time - b.time);

        // Group by season and find the earliest week for each season
        const seasonGroups = new Map<number, typeof dataPoints[0]>();
        for (const dp of sortedDataPoints) {
            const existing = seasonGroups.get(dp.season);
            if (!existing || dp.week < existing.week) {
                seasonGroups.set(dp.season, dp);
            }
        }

        // Create labels for each season at their earliest week
        const labeledPoints: { x: number; y: number; label: string; color: string }[] = [];
        const sortedSeasons = Array.from(seasonGroups.entries()).sort((a, b) => a[0] - b[0]);

        for (const [season, dp] of sortedSeasons) {
            const careerInfo = careerHistory.find(h => h.season === season);
            if (careerInfo) {
                const teamName = careerInfo.teamName;
                const color = teamColorsMap[teamName] || '#fff';
                labeledPoints.push({
                    x: getX(dp.time),
                    y: getY(dp.value),
                    label: `S${season} W${dp.week}`,
                    color,
                });
            }
        }

        const lineSegments: { points: string; color: string }[] = [];
        for (let i = 0; i < dataPoints.length - 1; i++) {
            const startPoint = dataPoints[i];
            const endPoint = dataPoints[i + 1];

            const careerInfo = careerHistory.find(h => h.season === startPoint.season);
            const teamName = careerInfo?.teamName || '';
            const color = teamColorsMap[teamName] || '#fff';

            lineSegments.push({
                points: `${getX(startPoint.time)},${getY(startPoint.value)} ${getX(endPoint.time)},${getY(endPoint.value)}`,
                color: color
            });
        }


        return { PADDING, WIDTH, HEIGHT, getX, getY, minValue, maxValue, dataPoints, lineSegments, transferMarkers, labeledPoints };
    }, [marketValueHistory, careerHistory, transferHistory, teamColorsMap]);
    
    const handleMouseOver = (e: React.MouseEvent<SVGElement>, text: string) => {
        const svgRect = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
        if (svgRect) {
            setTooltip({ x: e.clientX, y: e.clientY, text });
        }
    };
    
    if (!chartData) {
        return <p className="text-secondary" style={{ textAlign: 'center', padding: '24px' }}>Not enough data to display market value history.</p>;
    }

    const { PADDING, WIDTH, HEIGHT, getX, getY, minValue, maxValue, dataPoints, lineSegments, transferMarkers, labeledPoints } = chartData;

    return (
        <div className="value-chart-container" onMouseLeave={() => setTooltip(null)}>
            {tooltip && (
                <div className="chart-tooltip" style={{ left: tooltip.x + 15, top: tooltip.y + 15, opacity: 1 }}>
                    <div dangerouslySetInnerHTML={{ __html: tooltip.text }} />
                </div>
            )}
            <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="value-chart-svg" style={{ height: '250px' }}>
                <defs>
                    <style>
                        {`
                        .chart-axis-text { font-family: var(--font-family); font-size: 10px; fill: var(--text-color); }
                        .value-chart-point-label { font-family: var(--font-family); font-size: 11px; fill: var(--text-color); }
                        .value-chart-transfer-marker { font-size: 16px; }
                        `}
                    </style>
                </defs>

                <line x1={PADDING.left} y1={PADDING.top} x2={PADDING.left} y2={HEIGHT - PADDING.bottom} className="value-chart-axis-y" />

                {Array.from({ length: 5 }).map((_, i) => {
                    const value = minValue + (i / 4) * (maxValue - minValue);
                    const y = getY(value);
                    return (
                        <g key={value}>
                            <text x={PADDING.left - 8} y={y + 4} textAnchor="end" className="value-chart-axis-text">{formatValue(value)}</text>
                        </g>
                    );
                })}

                {labeledPoints.map((p, i) => (
                    <text key={i} x={p.x + 10} y={p.y - 10} className="value-chart-point-label" fill="var(--text-color)">{p.label}</text>
                ))}

                {lineSegments.map((segment, i) => (
                    <polyline key={i} points={segment.points} className="value-chart-line" stroke={segment.color} />
                ))}

                {dataPoints.map(d => (
                    <circle
                        key={d.time}
                        cx={getX(d.time)}
                        cy={getY(d.value)}
                        r="3"
                        className="value-chart-data-point"
                        onMouseOver={(e) => handleMouseOver(e, `<strong>${formatValue(d.value)}</strong><br/>S${d.season} W${d.week}`)}
                    />
                ))}
                
                {transferMarkers.map((t, i) => (
                    <text 
                        key={`transfer-${i}`}
                        x={getX(t.time)}
                        y={HEIGHT - PADDING.bottom + 20}
                        textAnchor="middle"
                        className="value-chart-transfer-marker"
                        onMouseOver={(e) => handleMouseOver(e, `<strong>Transfer</strong><br/>From: ${t.from}<br/>To: ${t.to}<br/>Fee: ${formatValue(t.fee)}`)}
                    >
                       {t.from === 'Youth Academy' ? '🌱' : '🔄'}
                    </text>
                ))}
            </svg>
        </div>
    );
};